import { Injectable, signal } from '@angular/core';

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: number;
  kind: ToastKind;
  title: string;
  message: string;
}

export interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export interface ChooseAction {
  id: string;
  label: string;
  variant?: 'primary' | 'danger' | 'ghost';
}

export interface ChooseRequest {
  title: string;
  message: string;
  actions: ChooseAction[];
}

@Injectable({ providedIn: 'root' })
export class UiFeedbackService {
  private nextId = 1;
  private timers = new Map<number, ReturnType<typeof setTimeout>>();

  readonly toasts = signal<ToastMessage[]>([]);
  readonly dialog = signal<(ConfirmRequest & { mode: 'confirm' }) | (ChooseRequest & { mode: 'choose' }) | null>(
    null,
  );

  private dialogResolver: ((value: boolean | string | null) => void) | null = null;

  success(message: string, title = 'Listo'): void {
    this.push('success', title, message);
  }

  error(message: string, title = 'Error'): void {
    this.push('error', title, message);
  }

  info(message: string, title = 'Aviso'): void {
    this.push('info', title, message);
  }

  /** Extrae el detalle de un error HTTP o usa el texto de respaldo. */
  httpError(err: unknown, fallback: string): void {
    const detail = (err as { error?: { detail?: string } })?.error?.detail;
    this.error(detail?.trim() || fallback);
  }

  dismiss(id: number): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    this.toasts.update((list) => list.filter((t) => t.id !== id));
  }

  confirm(request: ConfirmRequest): Promise<boolean> {
    return new Promise((resolve) => {
      this.closeDialog(null);
      this.dialogResolver = (value) => resolve(value === true);
      this.dialog.set({
        mode: 'confirm',
        title: request.title,
        message: request.message,
        confirmLabel: request.confirmLabel ?? 'Confirmar',
        cancelLabel: request.cancelLabel ?? 'Cancelar',
        danger: request.danger ?? false,
      });
    });
  }

  choose(request: ChooseRequest): Promise<string | null> {
    return new Promise((resolve) => {
      this.closeDialog(null);
      this.dialogResolver = (value) =>
        resolve(typeof value === 'string' ? value : null);
      this.dialog.set({
        mode: 'choose',
        title: request.title,
        message: request.message,
        actions: request.actions,
      });
    });
  }

  resolveDialog(value: boolean | string | null): void {
    this.closeDialog(value);
  }

  private closeDialog(value: boolean | string | null): void {
    const resolver = this.dialogResolver;
    this.dialogResolver = null;
    this.dialog.set(null);
    resolver?.(value);
  }

  private push(kind: ToastKind, title: string, message: string): void {
    const id = this.nextId++;
    this.toasts.update((list) => [...list, { id, kind, title, message }]);
    const timer = setTimeout(() => this.dismiss(id), kind === 'error' ? 5500 : 3500);
    this.timers.set(id, timer);
  }
}
